import { useMemo, type JSX, type MouseEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, PanelRight, Users, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ColumnPicker } from '@/components/ui/column-picker';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { RangeSelectHint, RowCheckbox, SelectAllCheckbox } from '@/components/ui/range-select';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
  DataTableSkeleton,
} from '@/components/ui/data-table';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { useRangeSelection } from '@/hooks/useRangeSelection';
import { LEAD_COLUMNS } from './columns';
import {
  columnsForScope,
  nextSort,
  sortDirection,
  toColumnDefs,
  visibleColumns,
  type TableScope,
} from './columnModel';
import type { LeadRow } from './rows';
import {
  canSelectAllMatching,
  isRowSelected,
  pageCheckState,
  selectAllMatching,
  selectedCount,
  setRows,
  togglePage,
  emptySelection,
  type SelectionState,
} from './selection';

export interface LeadTableProps {
  scope: TableScope;
  /** localStorage key for the column layout. Bump the version to reset saved layouts. */
  tableId: string;
  rows: LeadRow[];
  total: number;
  loading?: boolean;
  error?: Error | null;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  /** Server sort param; omit `onSortChange` for a table the server can't sort. */
  sort?: string;
  onSortChange?: (sort: string) => void;
  selection: SelectionState<LeadRow>;
  onSelectionChange: (next: SelectionState<LeadRow>) => void;
  onOpenRow: (row: LeadRow) => void;
  /** Rendered in the bulk bar when something is selected. */
  bulkActions?: ReactNode;
  /** Left of the Columns picker. */
  toolbar?: ReactNode;
  emptyTitle?: string;
  emptyHint?: string;
  /** Called instead of the empty state when there are no rows at all (no filters). */
  noun?: string;
}

/**
 * The shared lead table: a folder's Leads tab and the Creators screen.
 * Columns come from one registry; paging, sorting and filtering are the
 * server's; selection can span pages or be "every row matching".
 */
export function LeadTable({
  scope,
  tableId,
  rows,
  total,
  loading,
  error,
  page,
  limit,
  onPageChange,
  sort,
  onSortChange,
  selection,
  onSelectionChange,
  onOpenRow,
  bulkActions,
  toolbar,
  emptyTitle = 'No leads match these filters',
  emptyHint = 'Try widening the filters.',
  noun = 'lead',
}: LeadTableProps): JSX.Element {
  const columns = useMemo(() => columnsForScope(LEAD_COLUMNS, scope), [scope]);
  const defs = useMemo(() => toColumnDefs(columns), [columns]);
  const prefs = useColumnPreferences(tableId, defs);
  const shown = visibleColumns(columns, prefs.visible);

  const page$ = useMemo(() => rows.map((row) => ({ key: row.key, row })), [rows]);
  const pageKeys = useMemo(() => rows.map((r) => r.key), [rows]);
  const headState = pageCheckState(selection, pageKeys);
  const count = selectedCount(selection, total);
  const offerAll = canSelectAllMatching(selection, pageKeys, total);

  // Shift-click ranges over this page. A row click opens the creator sheet,
  // so the checkbox (with a padded hit area) is the selection target.
  const range = useRangeSelection({
    visible: pageKeys,
    isSelected: (key) => isRowSelected(selection, key),
    onApply: (keys, checked) => onSelectionChange(setRows(selection, keys, checked, page$)),
  });

  function onRowClick(ev: MouseEvent<HTMLTableRowElement>, row: LeadRow) {
    const target = ev.target as HTMLElement;
    // Links, buttons, checkboxes and anything portalled keep their own click.
    if (target.closest('a,button,input,label,[role="menuitem"],[role="dialog"]')) return;
    if (!ev.currentTarget.contains(target)) return;
    onOpenRow(row);
  }

  const plural = (n: number) => `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;

  return (
    <div className="space-y-2">
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-2">
        {count > 0 ? (
          <div
            role="region"
            aria-label="Bulk actions"
            className="flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand/10 px-2 py-1"
          >
            <span className="px-1 text-sm font-semibold">
              {selection.mode === 'all' ? `All ${plural(count)} matching` : `${plural(count)} selected`}
            </span>
            {bulkActions}
            <RangeSelectHint show={selection.mode === 'rows'} />
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => onSelectionChange(emptySelection(selection.filterKey))}
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground tabular-nums">{loading && !rows.length ? 'Loading…' : plural(total)}</p>
        )}
        <div className="flex items-center gap-2">
          {toolbar}
          <ColumnPicker columns={defs} visible={prefs.visible} onToggle={prefs.toggle} />
        </div>
      </div>

      {offerAll && (
        <p className="text-sm text-muted-foreground">
          All {rows.length} on this page are selected.{' '}
          <button
            type="button"
            className="font-semibold text-brand hover:underline"
            onClick={() => onSelectionChange(selectAllMatching(selection))}
          >
            Select all {plural(total)} matching
          </button>
        </p>
      )}

      {error && <p className="text-sm text-destructive-strong">{error.message}</p>}

      {!loading && rows.length === 0 ? (
        <div className="table-frame">
          <EmptyState icon={<Users className="h-6 w-6" strokeWidth={1.5} />} title={emptyTitle} hint={emptyHint} />
        </div>
      ) : (
        <DataTable aria-label={scope === 'folder' ? 'Leads' : 'Creators'} aria-busy={loading || undefined}>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell narrow>
                <SelectAllCheckbox
                  label="Select this page"
                  state={headState}
                  onToggle={() => onSelectionChange(togglePage(selection, page$))}
                />
              </DataTableHeaderCell>
              {shown.map((c) => (
                <DataTableHeaderCell
                  key={c.id}
                  numeric={c.numeric}
                  aria-sort={c.sortKey && onSortChange ? ariaSort(sortDirection(sort ?? '', c.sortKey)) : undefined}
                >
                  {c.sortKey && onSortChange ? (
                    <SortButton
                      label={c.header}
                      dir={sortDirection(sort ?? '', c.sortKey)}
                      onClick={() => onSortChange(nextSort(sort ?? '', c.sortKey!))}
                    />
                  ) : (
                    c.header
                  )}
                </DataTableHeaderCell>
              ))}
              <DataTableHeaderCell narrow>
                <span className="sr-only">Open</span>
              </DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          {loading && rows.length === 0 ? (
            <DataTableSkeleton cols={['w-4', ...shown.map(() => 'w-20'), 'w-4']} />
          ) : (
            <tbody className={cn(loading && 'opacity-60 transition-opacity')}>
              {rows.map((row) => {
                const on = isRowSelected(selection, row.key);
                return (
                  <DataTableRow
                    key={row.key}
                    state={on ? 'selected' : row.doneReason ? 'terminal' : 'default'}
                    className="cursor-pointer"
                    onClick={(ev) => onRowClick(ev, row)}
                  >
                    <DataTableCell narrow>
                      <RowCheckbox aria-label={`Select @${row.username}`} hitClassName="-mx-3 -my-2 px-3 py-2" {...range.checkboxProps(row.key)} />
                    </DataTableCell>
                    {shown.map((c) => (
                      <DataTableCell key={c.id} numeric={c.numeric} anchor={c.id === 'creator'} data-label={c.header}>
                        {c.cell(row)}
                      </DataTableCell>
                    ))}
                    <DataTableCell actions>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label={`Open @${row.username}`}
                        title={`Open @${row.username}`}
                        onClick={() => onOpenRow(row)}
                      >
                        <PanelRight className="h-4 w-4" />
                      </Button>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </tbody>
          )}
        </DataTable>
      )}

      <Pagination page={page} limit={limit} total={total} onPageChange={onPageChange} />
    </div>
  );
}

function ariaSort(dir: 'asc' | 'desc' | null): 'ascending' | 'descending' | 'none' {
  return dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none';
}

function SortButton({ label, dir, onClick }: { label: string; dir: 'asc' | 'desc' | null; onClick: () => void }): JSX.Element {
  const Icon: LucideIcon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('inline-flex items-center gap-1 hover:text-foreground', dir && 'text-foreground')}
    >
      {label}
      <Icon className={cn('h-3 w-3', !dir && 'opacity-40')} />
    </button>
  );
}

export interface BulkAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  title?: string;
}

/** The bulk bar's buttons. Compact, labelled, destructive ones last. */
export function BulkActions({ actions }: { actions: readonly BulkAction[] }): JSX.Element {
  const ordered = [...actions.filter((a) => !a.destructive), ...actions.filter((a) => a.destructive)];
  return (
    <>
      {ordered.map((a) => (
        <Button
          key={a.label}
          size="sm"
          variant={a.destructive ? 'ghost' : 'outline'}
          className={cn(a.destructive && 'text-destructive-strong hover:text-destructive-strong')}
          disabled={a.disabled}
          title={a.title}
          onClick={a.onClick}
        >
          <a.icon className="h-3.5 w-3.5" />
          {a.label}
        </Button>
      ))}
    </>
  );
}
